import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Copy,
  Check,
  Eye,
  Clock,
  Users,
  MousePointer,
  Monitor,
  Smartphone,
  Tablet,
} from 'lucide-react'
import { linksApi, analyticsApi } from '../services/api'
import type { TrackingLink, ViewEvent } from '../types'
import { Tabs, Spinner, EmptyState, StatCard } from '../components/common'
import { ViewsLineChart } from '../components/AnalyticsChart'
import { formatNumber, formatDuration, useClipboard } from '../hooks'
import { format, formatDistanceToNow } from 'date-fns'

export default function LinkDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [link, setLink] = useState<TrackingLink | null>(null)
  const [events, setEvents] = useState<ViewEvent[]>([])
  const [analytics, setAnalytics] = useState<Record<string, unknown> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('overview')
  const { copied, copy } = useClipboard()

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  const loadData = async () => {
    if (!id) return

    setIsLoading(true)
    setError(null)

    try {
      const [linkData, eventsData, analyticsData] = await Promise.all([
        linksApi.get(id),
        linksApi.getEvents(id),
        analyticsApi.getLinkAnalytics(id),
      ])

      setLink(linkData)
      setEvents(eventsData.events)
      setAnalytics(analyticsData)
    } catch (err) {
      setError('Failed to load link details')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleActive = async () => {
    if (!id || !link) return
    setIsUpdating(true)
    try {
      const updatedLink = await linksApi.update(id, { is_active: !link.is_active })
      setLink(updatedLink)
    } catch (err) {
      setError('Failed to update link')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRegenerateCode = async () => {
    if (!id) return
    if (!confirm('Are you sure you want to regenerate the link code? The old link will stop working.')) {
      return
    }
    setIsUpdating(true)
    try {
      const updatedLink = await linksApi.regenerateCode(id)
      setLink(updatedLink)
    } catch (err) {
      setError('Failed to regenerate code')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteLink = async () => {
    if (!id || !link) return
    if (!confirm('Are you sure you want to delete this link? This action cannot be undone.')) {
      return
    }
    setIsUpdating(true)
    try {
      await linksApi.delete(id)
      navigate(`/videos/${link.video_id}`)
    } catch (err) {
      setError('Failed to delete link')
      setIsUpdating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !link) {
    return (
      <EmptyState
        title="Link not found"
        description={error || 'The link you are looking for does not exist'}
        action={
          <Link to="/videos" className="btn-primary">
            Back to Videos
          </Link>
        }
      />
    )
  }

  const summary = (analytics as { summary?: Record<string, number> })?.summary || {}

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'views', label: `Views (${events.length})` },
    { id: 'settings', label: 'Settings' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/videos/${link.video_id}`}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">
            {link.recipient_name || `Link ${link.short_code}`}
          </h1>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-sm text-slate-400">{link.recipient_email}</span>
            {link.recipient_company && (
              <span className="text-sm text-slate-400">{link.recipient_company}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-700 rounded-lg px-3 py-2">
            <span className="text-sm text-slate-300 mr-2 max-w-xs truncate">
              {link.short_url}
            </span>
            <button
              onClick={() => copy(link.short_url)}
              className="text-slate-400 hover:text-white"
            >
              {copied ? (
                <Check className="w-4 h-4 text-emerald-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Views"
          value={formatNumber(summary.total_views || link.view_count)}
          icon={<Eye className="w-5 h-5" />}
        />
        <StatCard
          label="Unique Viewers"
          value={formatNumber(summary.unique_viewers || link.unique_viewers)}
          icon={<Users className="w-5 h-5" />}
        />
        <StatCard
          label="Avg. Completion"
          value={`${Math.round(summary.avg_completion || link.avg_completion)}%`}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          label="CTA Clicks"
          value={summary.cta_clicks || 0}
          icon={<MousePointer className="w-5 h-5" />}
        />
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Views chart */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">Views Over Time</h3>
              {events.length > 0 ? (
                <ViewsLineChart
                  data={events.map((e) => ({
                    date: format(new Date(e.started_at), 'MMM d'),
                    views: 1,
                  }))}
                  dataKey="views"
                  xDataKey="date"
                  height={200}
                />
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">
                  No views yet
                </div>
              )}
            </div>

            {/* Link info */}
            <div className="card space-y-4">
              <h3 className="text-lg font-semibold text-white">Link Details</h3>

              {link.custom_message && (
                <div>
                  <label className="text-sm text-slate-400">Custom Message</label>
                  <p className="text-white mt-1">{link.custom_message}</p>
                </div>
              )}

              {link.custom_cta_url && (
                <div>
                  <label className="text-sm text-slate-400">CTA Button</label>
                  <p className="text-white mt-1">
                    {link.custom_cta_text || 'Learn More'} → {link.custom_cta_url}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm text-slate-400">Created</label>
                <p className="text-white mt-1">
                  {format(new Date(link.created_at), 'PPP')}
                </p>
              </div>

              {link.last_viewed_at && (
                <div>
                  <label className="text-sm text-slate-400">Last Viewed</label>
                  <p className="text-white mt-1">
                    {format(new Date(link.last_viewed_at), 'PPP p')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'views' && (
          <div className="space-y-4">
            {events.length === 0 ? (
              <EmptyState
                icon={<Eye className="w-12 h-12" />}
                title="No views yet"
                description="Share your link to start tracking views"
              />
            ) : (
              <>
                {/* Summary metrics for views tab */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="card bg-slate-800/50">
                    <div className="text-sm text-slate-400">Total Watch Time</div>
                    <div className="text-xl font-semibold text-white mt-1">
                      {formatDuration(events.reduce((acc, e) => acc + (e.watch_time || 0), 0))}
                    </div>
                  </div>
                  <div className="card bg-slate-800/50">
                    <div className="text-sm text-slate-400">Avg. Watch Time</div>
                    <div className="text-xl font-semibold text-white mt-1">
                      {formatDuration(events.length > 0 ? events.reduce((acc, e) => acc + (e.watch_time || 0), 0) / events.length : 0)}
                    </div>
                  </div>
                  <div className="card bg-slate-800/50">
                    <div className="text-sm text-slate-400">Avg. Completion</div>
                    <div className="text-xl font-semibold text-white mt-1">
                      {events.length > 0 ? Math.round(events.reduce((acc, e) => acc + e.completion_percent, 0) / events.length) : 0}%
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {events.map((event) => {
                    const DeviceIcon = event.device_type === 'mobile' ? Smartphone
                      : event.device_type === 'tablet' ? Tablet
                      : Monitor

                    return (
                      <div key={event.id} className="card flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                            <DeviceIcon className="w-5 h-5 text-slate-400" />
                          </div>

                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">
                                {event.city || event.country_name || 'Unknown location'}
                              </span>
                              {event.country && (
                                <span>
                                  {String.fromCodePoint(
                                    ...event.country
                                      .toUpperCase()
                                      .split('')
                                      .map((char) => 127397 + char.charCodeAt(0))
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-400">
                              {event.browser} on {event.os} • {event.device_type || 'unknown'}
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-white flex items-center gap-2 justify-end">
                            <span>{Math.round(event.completion_percent)}% watched</span>
                            {event.watch_time > 0 && (
                              <span className="text-slate-400 text-sm">
                                ({formatDuration(event.watch_time)})
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-400">
                            {formatDistanceToNow(new Date(event.started_at), { addSuffix: true })}
                            <span className="mx-1">•</span>
                            {format(new Date(event.started_at), 'MMM d, h:mm a')}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-white">Link Active</h4>
                <p className="text-sm text-slate-400">
                  When disabled, viewers will see an error page
                </p>
              </div>
              <button
                onClick={handleToggleActive}
                disabled={isUpdating}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  link.is_active ? 'bg-primary-600' : 'bg-slate-600'
                } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    link.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <hr className="border-slate-700" />

            <div className="flex gap-3">
              <button
                onClick={handleRegenerateCode}
                disabled={isUpdating}
                className="btn-secondary"
              >
                {isUpdating ? 'Processing...' : 'Regenerate Code'}
              </button>
              <button
                onClick={handleDeleteLink}
                disabled={isUpdating}
                className="btn-danger"
              >
                Delete Link
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
